import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, ExternalLink, Sparkles, Palette, Eye, Coins, ArrowRight, Send, History, Pencil, Check as CheckIcon, X as XIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_GROUPS = [
  {
    title: "Hero banner",
    fields: [
      { k: "hero_badge",           label: "Badge (topo)",          type: "input",    hint: "Ex.: Ganhe agora" },
      { k: "hero_title",           label: "Título principal",      type: "textarea", hint: "Frase que aparece em destaque" },
      { k: "hero_copy_prefix",     label: "Texto de apoio",        type: "input",    hint: "Aparece logo abaixo do título" },
      { k: "hero_copy_cta_inline", label: "Frase inline",          type: "input",    hint: "Texto em negrito antes da seta" },
      { k: "hero_cta",             label: "Rótulo do botão CTA",   type: "input",    hint: "Ex.: Criar conta grátis" },
      { k: "hero_footnote",        label: "Nota de rodapé",        type: "input",    hint: "Aparece ao lado do CTA (desktop)" },
      { k: "hero_referral_badge",  label: "Badge de convite (?ref=)", type: "input", hint: "Aparece quando o link tem ?ref=" },
    ],
  },
  {
    title: "Contador ao vivo",
    fields: [
      { k: "live_counter_label",   label: "Rótulo do contador",    type: "input" },
      { k: "live_counter_unit",    label: "Unidade",               type: "input", hint: "Ex.: moedas distribuídas" },
    ],
  },
  {
    title: "Seção principal (abaixo do hero)",
    fields: [
      { k: "section_intro_label",  label: "Selo introdutório",     type: "input", hint: "Ex.: Novo · 2026" },
      { k: "main_heading",         label: "Título da seção",       type: "textarea" },
      { k: "main_subheading",      label: "Subtítulo da seção",    type: "textarea" },
    ],
  },
  {
    title: "Depoimentos",
    fields: [
      { k: "testimonials_subtitle", label: "Selo (acima do título)", type: "input" },
      { k: "testimonials_headline", label: "Título do carrossel",   type: "input" },
    ],
  },
];

const COLOR_OPTS = [
  { k: "amber",   sw: "bg-amber-500" },
  { k: "emerald", sw: "bg-emerald-500" },
  { k: "green",   sw: "bg-green-500" },
  { k: "blue",    sw: "bg-blue-500" },
  { k: "violet",  sw: "bg-violet-500" },
  { k: "red",     sw: "bg-red-500" },
  { k: "slate",   sw: "bg-slate-500" },
];

export default function AdminLandingContent() {
  const [defaults, setDefaults] = useState({});
  const [current, setCurrent] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [restoring, setRestoring] = useState("");
  const [renamingId, setRenamingId] = useState("");
  const [renameValue, setRenameValue] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/landing/content`);
      setDefaults(r.data);
      setCurrent(r.data);
      setLoaded(true);
      loadHistory();
    } catch (e) {
      toast.error("Falha ao carregar conteúdo");
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/admin/landing/history`);
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch { setHistory([]); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(
    () => JSON.stringify(defaults) !== JSON.stringify(current),
    [defaults, current],
  );

  const set = (k, v) => setCurrent((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/landing/content`, current);
      setDefaults(r.data.content);
      setCurrent(r.data.content);
      loadHistory();
      toast.success("Conteúdo da landing atualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const restore = async (snap) => {
    if (!snap?.snapshot_id) return;
    setRestoring(snap.snapshot_id);
    try {
      const r = await axios.post(`${API}/admin/landing/restore/${snap.snapshot_id}`);
      setDefaults(r.data.content);
      setCurrent(r.data.content);
      loadHistory();
      toast.success("Versão restaurada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível restaurar");
    }
    setRestoring("");
  };

  const startRename = (snap) => {
    setRenamingId(snap.snapshot_id);
    setRenameValue(snap.label || "");
  };

  const saveRename = async () => {
    if (!renamingId) return;
    try {
      await axios.post(`${API}/admin/landing/history/${renamingId}/rename`, { label: renameValue.trim() });
      toast.success("Nome atualizado");
      setRenamingId("");
      setRenameValue("");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao renomear");
    }
  };

  const resetToDefaults = async () => {
    // Push all defaults from backend (empty payload = keep all current). Instead,
    // clear the stored doc by pushing every known key with the default from the
    // in-memory copy we loaded on first mount (that was already merged with defaults).
    // Simplest UX: revert unsaved changes to the last-saved value.
    setCurrent(defaults);
    toast.info("Alterações descartadas");
  };

  if (!loaded) {
    return (
      <Card className="p-6 flex items-center gap-2" data-testid="landing-content-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
      </Card>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]" data-testid="admin-landing-content-shell">
      <div className="space-y-6 min-w-0" data-testid="admin-landing-content">
      <Card className="p-5 flex items-start gap-3 border-primary/20 bg-primary/5">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Palette className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-bold">Landing · sem código</div>
          <h2 className="text-lg font-bold tracking-tight">Textos e banners da página inicial</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tudo aqui aparece publicamente em <a href="/" target="_blank" rel="noreferrer" className="underline">Landing</a> em segundos após salvar.
          </p>
        </div>
      </Card>

      {/* Announcement */}
      <Card className="p-5" data-testid="admin-landing-announcement">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="font-semibold text-sm">Barra de aviso no topo da landing</div>
                <div className="text-[11px] text-muted-foreground">Ativa para promoções relâmpago. Aparece antes do hero.</div>
              </div>
              <Switch
                checked={!!current.announcement_enabled}
                onCheckedChange={(v) => set("announcement_enabled", v)}
                data-testid="landing-announce-enabled"
              />
            </div>
            {current.announcement_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Texto</span>
                  <Input
                    value={current.announcement_text || ""}
                    onChange={(e) => set("announcement_text", e.target.value)}
                    maxLength={200}
                    placeholder="Ex.: 🎉 Bônus especial no primeiro convite hoje!"
                    data-testid="landing-announce-text"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Link (opcional)</span>
                  <Input
                    value={current.announcement_href || ""}
                    onChange={(e) => set("announcement_href", e.target.value)}
                    placeholder="https://…"
                    data-testid="landing-announce-href"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Cor</span>
                  <div className="flex gap-1.5 flex-wrap" data-testid="landing-announce-colors">
                    {COLOR_OPTS.map((c) => (
                      <button
                        key={c.k}
                        type="button"
                        onClick={() => set("announcement_color", c.k)}
                        className={`w-7 h-7 rounded-full ${c.sw} ring-2 ring-offset-2 ring-offset-background transition ${current.announcement_color === c.k ? "ring-foreground" : "ring-transparent"}`}
                        title={c.k}
                        data-testid={`landing-announce-color-${c.k}`}
                      />
                    ))}
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Field groups */}
      {FIELD_GROUPS.map((g) => (
        <Card key={g.title} className="p-5" data-testid={`landing-group-${g.title}`}>
          <h3 className="text-sm font-bold tracking-tight mb-4">{g.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map((f) => (
              <label key={f.k} className={`flex flex-col gap-1 ${f.type === "textarea" ? "md:col-span-2" : ""}`}>
                <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{f.label}</span>
                {f.type === "textarea" ? (
                  <Textarea
                    value={current[f.k] || ""}
                    onChange={(e) => set(f.k, e.target.value)}
                    rows={2}
                    maxLength={320}
                    data-testid={`landing-field-${f.k}`}
                  />
                ) : (
                  <Input
                    value={current[f.k] || ""}
                    onChange={(e) => set(f.k, e.target.value)}
                    maxLength={200}
                    data-testid={`landing-field-${f.k}`}
                  />
                )}
                {f.hint && <span className="text-[10px] text-muted-foreground">{f.hint}</span>}
              </label>
            ))}
          </div>
        </Card>
      ))}

      {/* Sticky save bar */}
      <div className="sticky bottom-4 z-10 flex items-center justify-between gap-2 rounded-2xl border bg-background/95 backdrop-blur px-4 py-3 shadow-lg">
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          {dirty ? <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> : <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
          {dirty ? "Alterações não salvas" : "Tudo salvo"}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults} disabled={!dirty || saving} className="rounded-full gap-1" data-testid="landing-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Descartar
          </Button>
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline pr-1" data-testid="landing-preview">
            Ver landing <ExternalLink className="w-3 h-3" />
          </a>
          <Button onClick={save} disabled={!dirty || saving} className="rounded-full gap-1" data-testid="landing-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </Button>
        </div>
      </div>
      </div>

      {/* Live preview panel */}
      <aside className="xl:sticky xl:top-4 h-fit" data-testid="admin-landing-preview">
        <Card className="p-4 space-y-3 border-primary/20 bg-card/60 backdrop-blur">
          <div className="flex items-center justify-between gap-2">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-primary font-bold">
              <Eye className="w-3.5 h-3.5" /> Preview ao vivo
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${dirty ? "bg-amber-500/15 text-amber-700" : "bg-emerald-500/15 text-emerald-700"}`}>
              {dirty ? "não salvo" : "espelha a landing"}
            </span>
          </div>

          {/* announcement bar preview */}
          {current.announcement_enabled && current.announcement_text && (() => {
            const CMAP = {
              amber:   { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.45)", fg: "#78350f" },
              emerald: { bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.45)", fg: "#064e3b" },
              green:   { bg: "rgba(34,197,94,0.14)",  border: "rgba(34,197,94,0.45)",  fg: "#14532d" },
              blue:    { bg: "rgba(37,99,235,0.14)",  border: "rgba(37,99,235,0.45)",  fg: "#1e3a8a" },
              violet:  { bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.45)", fg: "#4c1d95" },
              red:     { bg: "rgba(220,38,38,0.14)",  border: "rgba(220,38,38,0.45)",  fg: "#7f1d1d" },
              slate:   { bg: "rgba(100,116,139,0.14)",border: "rgba(100,116,139,0.45)",fg: "#0f172a" },
            };
            const p = CMAP[current.announcement_color] || CMAP.amber;
            return (
              <div
                className="rounded-lg px-3 py-1.5 text-center text-[11px] font-semibold border"
                style={{ backgroundColor: p.bg, borderColor: p.border, color: p.fg }}
                data-testid="preview-announcement"
              >
                {current.announcement_text}
              </div>
            );
          })()}

          {/* hero preview */}
          <div
            className="rounded-2xl relative overflow-hidden text-white"
            style={{ background: "linear-gradient(120deg, #f59e0b 0%, #f97316 25%, #dc2626 55%, #7c3aed 85%, #2563eb 100%)" }}
            data-testid="preview-hero"
          >
            <div className="p-4">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 border border-white/25 text-[9px] font-bold uppercase tracking-widest mb-2">
                <Sparkles className="w-3 h-3" /> {current.hero_badge || "Ganhe agora"}
              </div>
              <div className="font-black text-[15px] leading-tight drop-shadow" data-testid="preview-hero-title">
                {current.hero_title || "No Futuro PDV você ganha moedas por tudo que faz."}
              </div>
              <p className="text-white/90 text-[11px] mt-1 leading-snug">
                {current.hero_copy_prefix || "Transfira para seus amigos na hora."}{" "}
                <span className="font-bold inline-flex items-center gap-0.5">
                  {current.hero_copy_cta_inline || "Vem para o futuro!"} <ArrowRight className="w-3 h-3" />
                </span>
              </p>
              <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-black/30 px-2 py-1 border border-white/15">
                <span className="w-6 h-6 rounded-md bg-yellow-300 text-yellow-950 flex items-center justify-center"><Coins className="w-3 h-3" /></span>
                <div className="leading-tight">
                  <div className="text-[8px] uppercase tracking-widest text-white/70 font-bold">
                    {current.live_counter_label || "Ao vivo · últimas 24h"}
                  </div>
                  <div className="text-[11px] font-black">
                    3.633 <span className="text-[10px] font-bold text-white/80">{current.live_counter_unit || "moedas distribuídas"}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 inline-flex items-center gap-1 h-7 px-3 rounded-full bg-white text-slate-900 text-[11px] font-bold shadow" data-testid="preview-hero-cta">
                <Send className="w-3 h-3" /> {current.hero_cta || "Criar conta grátis"}
              </div>
            </div>
          </div>

          {/* section preview */}
          <div className="p-3 rounded-xl border border-border bg-card">
            <div className="text-[9px] uppercase tracking-widest text-primary font-bold" data-testid="preview-section-intro">
              {current.section_intro_label || "Novo · 2026"}
            </div>
            <div className="font-black text-sm mt-1 leading-tight" data-testid="preview-main-heading">
              {current.main_heading || "No FUTURO PDV, tudo que você faz vira dinheiro"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="preview-main-subheading">
              {current.main_subheading || "Curta, compartilhe, trabalhe, dirija — acumule moedas e saque na hora."}
            </div>
          </div>

          {/* testimonials header preview */}
          <div className="p-3 rounded-xl border border-border bg-card">
            <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {current.testimonials_subtitle || "Prova social · últimos 7 dias"}
            </div>
            <div className="font-bold text-sm mt-1" data-testid="preview-testimonials-headline">
              {current.testimonials_headline || "Pessoas reais ganhando com o Futuro PDV"}
            </div>
          </div>

          {/* Version history */}
          {history.length > 0 && (
            <div className="border-t border-border pt-3" data-testid="landing-history-panel">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1">
                <History className="w-3 h-3" /> Últimas versões salvas
              </div>
              <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
                {history.map((h) => {
                  const when = new Date(h.created_at).toLocaleString("pt-BR");
                  const isRenaming = renamingId === h.snapshot_id;
                  return (
                    <li key={h.snapshot_id} className="p-2 rounded-lg border border-border/60 bg-muted/40" data-testid={`landing-history-${h.snapshot_id}`}>
                      {isRenaming ? (
                        <div className="flex items-center gap-1.5" data-testid={`landing-history-rename-${h.snapshot_id}`}>
                          <Input
                            autoFocus
                            value={renameValue}
                            maxLength={80}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenamingId(""); }}
                            placeholder="Ex.: Campanha Black Friday"
                            className="h-7 text-xs flex-1"
                            data-testid={`landing-history-rename-input-${h.snapshot_id}`}
                          />
                          <button
                            onClick={saveRename}
                            className="p-1 rounded hover:bg-muted"
                            data-testid={`landing-history-rename-save-${h.snapshot_id}`}
                            aria-label="Salvar nome"
                          >
                            <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
                          </button>
                          <button
                            onClick={() => setRenamingId("")}
                            className="p-1 rounded hover:bg-muted"
                            aria-label="Cancelar"
                          >
                            <XIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 min-w-0" data-testid={`landing-history-header-${h.snapshot_id}`}>
                          {h.label ? (
                            <span className="text-[11px] font-bold text-primary truncate" title={h.label}>{h.label}</span>
                          ) : (
                            <span className="text-[11px] font-semibold truncate" title={h.preview?.hero_title}>
                              {h.preview?.hero_title || "(sem título)"}
                            </span>
                          )}
                          <button
                            onClick={() => startRename(h)}
                            className="p-0.5 rounded hover:bg-muted shrink-0"
                            title="Renomear"
                            data-testid={`landing-history-rename-start-${h.snapshot_id}`}
                          >
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </button>
                        </div>
                      )}
                      {h.label && !isRenaming && (
                        <div className="text-[10px] text-muted-foreground truncate mt-0.5" title={h.preview?.hero_title}>
                          {h.preview?.hero_title}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {when} · {h.saved_by_name || "admin"}
                        </span>
                        <button
                          onClick={() => restore(h)}
                          disabled={restoring === h.snapshot_id}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline disabled:opacity-60"
                          data-testid={`landing-history-restore-${h.snapshot_id}`}
                        >
                          {restoring === h.snapshot_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restaurar
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Card>
      </aside>
    </div>
  );
}
